import React from "react";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ImageUpload = ({
  value = null,
  onChange,
  accept = "image/*",
  maxSizeMB = 5,
}) => {
  const inputRef = React.useRef(null);
  const previewObjectUrlRef = React.useRef(null);

  const [preview, setPreview] = React.useState(null);

  React.useEffect(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }

    if (typeof value === "string") {
      setPreview(value);
    } else if (typeof Blob !== "undefined" && value instanceof Blob) {
      const imageUrl = URL.createObjectURL(value);
      previewObjectUrlRef.current = imageUrl;
      setPreview(imageUrl);
    } else {
      setPreview(null);
    }
  }, [value]);

  React.useEffect(() => () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validate image
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    // Validate size
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`Image size should be less than ${maxSizeMB} MB.`);
      return;
    }

    // Create preview
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }
    const imageUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = imageUrl;

    setPreview(imageUrl);

    // Send File object to parent
    onChange?.(file);
  };

  const handleRemove = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setPreview(null);

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    onChange?.(null);
  };

  return (
    <Box>
      {!preview ? (
        <Box
          sx={{
            border: "2px dashed #bdbdbd",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "#fafafa",
            },
          }}
          onClick={() => inputRef.current?.click()}
        >
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: 45,
              color: "primary.main",
              mb: 1,
            }}
          />

          <Typography variant="body1">
            Click to upload image
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            JPG, JPEG, PNG up to {maxSizeMB} MB
          </Typography>

          <input
            ref={inputRef}
            type="file"
            hidden
            accept={accept}
            onChange={handleFileChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            border: "1px solid #ddd",
            borderRadius: 2,
            p: 1,
            width: "fit-content",
          }}
        >
          <Box
            component="img"
            src={preview}
            alt="Preview"
            sx={{
              display: "block",
              width: 250,
              height: 180,
              objectFit: "contain",
              borderRadius: 1,
            }}
          />

          {/* Disabled the Delete button */}
          {/* <IconButton
            onClick={handleRemove}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <DeleteOutlineIcon />
          </IconButton> */}
        </Box>
      )}

      {/* {!preview && (
        <Button
          variant="outlined"
          startIcon={<ImageOutlinedIcon />}
          sx={{ mt: 2 }}
          onClick={() => inputRef.current?.click()}
        >
          Choose Image
        </Button>
      )} */}
    </Box>
  );
};

export default ImageUpload;